import { useUnit } from 'effector-react';
import $$api from 'models/api';
import $$trustFunding from 'models/pages/trust-funding';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import columns from './trust-funding-table-columns';

import HookFormControl from 'components/HookFormControl';
import Table from 'components/Table/Table';
import LoadingPage from 'components/pages/LoadingPage/LoadingPage';

import TrustFundingService from 'services/TrustFundingService';

import useResources from 'hooks/useResources';

import imgTrustFunding from 'assets/trust-funding.png';

const TrustFundingPage = () => {
    const { t } = useResources(['common', 'trust_fund']);

    const form = useForm();
    const filters = form.watch();

    const isAlreadyCreated = useUnit($$trustFunding.$isAlreadyCreated);
    const categories = useUnit($$trustFunding.$categories);
    const assets = useUnit($$trustFunding.$assets);
    const questionnaire = useUnit($$trustFunding.$questionnaire);
    const answers = useUnit($$trustFunding.$answers);
    const qInstance = useUnit($$trustFunding.$qInstance);

    const categoryOptions = categories.map((category) => ({ value: category, title: t(`trust_fund:${category}`) }));

    const [areAssetsProcessed, setAssetsProcessed] = useState(false);

    useEffect(() => {
        if (!questionnaire || !answers || !qInstance) {
            return;
        }
        (async () => {
            const newAnswers = TrustFundingService.removeUnfinishedAssets(answers, questionnaire);
            if (newAnswers.assets?._loop?.length !== answers.assets?._loop?.length) {
                await $$api.answers.putAnswersFx({
                    id: qInstance.questionnaire_instance_id,
                    answers: newAnswers,
                });
            }
            setAssetsProcessed(true);
        })();
    }, [assets.length]);

    if (assets?.length && !areAssetsProcessed) {
        return <LoadingPage />;
    }

    if (isAlreadyCreated && assets?.length) {
        return (
            <>
                <p>{t('trustFunding.alreadyCreated.description')}</p>
                <FormProvider {...form}>
                    <div className="row gap-3">
                        <HookFormControl.Text
                            name="name"
                            className="form-control col-4"
                            placeholder={t('trustFunding.alreadyCreated.input.placeholder')}
                        />
                        <HookFormControl.Select
                            name="category"
                            items={categoryOptions}
                            addEmptyItem={true}
                            emptyItemTitle={t('trustFunding.alreadyCreated.select.placeholder')}
                            className="form-select col offset-2"
                        />
                        <button
                            className="btn btn-primary col-2"
                            type="button"
                            onClick={() => $$trustFunding.addAssetClicked()}
                        >
                            {t('trustFunding.alreadyCreated.button')}
                        </button>
                    </div>
                </FormProvider>
                <Table
                    columns={columns(t)}
                    rows={assets}
                    idColumnName="id"
                    name="TRUST_FUNDING"
                    filters={filters}
                    withPagination={false}
                    onRowClick={{ callback: (asset) => $$trustFunding.assetClicked(asset.id) }}
                />
                <div className="w-100 d-flex justify-content-end">
                    <button
                        className="btn btn-primary mt-3 w-auto"
                        onClick={() => $$trustFunding.printInstructionsButtonClicked()}
                    >
                        {t('trustFunding.alreadyCreated.printInstructions')}
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <p>{t('trustFunding.description')}</p>
            <div className="card">
                <img src={imgTrustFunding} className="img-fluid" alt="sign-in" />
                <div className="p-4 d-flex flex-column align-items-center justify-content-center">
                    <h3 className="text-center serif text-black">{t('trustFunding.getStarted.title')}</h3>
                    <p className="text-center text-secondary">{t('trustFunding.getStarted.description')}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            isAlreadyCreated
                                ? $$trustFunding.addAssetClicked()
                                : $$trustFunding.createQInstanceButtonClicked()
                        }
                    >
                        {t('trustFunding.getStarted.button')}
                    </button>
                </div>
            </div>
        </>
    );
};

export default TrustFundingPage;
